import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

type ScrollableContainerRef = React.RefObject<HTMLElement>

// Context for managing scrollable container refs
interface ScrollableContainerContextType {
  registerScrollableContainer: (ref: ScrollableContainerRef) => () => void
  getScrollableContainer: () => ScrollableContainerRef | null
}

const ScrollableContainerContext = createContext<ScrollableContainerContextType | null>(null)

export const useScrollableContainer = () => {
  const context = useContext(ScrollableContainerContext)
  if (!context) {
    throw new Error('useScrollableContainer must be used within ScrollToTop provider')
  }
  return context
}

// Hook for components to register their scrollable container
export const useRegisterScrollableContainer = (ref: ScrollableContainerRef) => {
  const { registerScrollableContainer } = useScrollableContainer()

  useEffect(() => {
    const unregister = registerScrollableContainer(ref)
    return unregister
  }, [ref, registerScrollableContainer])
}

export default function ScrollToTop({ children }: PropsWithChildren) {
  const { pathname } = useLocation()
  const scrollableContainerRef = useRef<ScrollableContainerRef | null>(null)
  const [, forceUpdate] = useState({})

  const registerScrollableContainer = useCallback((ref: ScrollableContainerRef) => {
    scrollableContainerRef.current = ref
    forceUpdate({})

    // Return unregister function
    return () => {
      if (scrollableContainerRef.current === ref) {
        scrollableContainerRef.current = null
        forceUpdate({})
      }
    }
  }, [])

  const getScrollableContainer = useCallback(() => {
    return scrollableContainerRef.current
  }, [])

  useEffect(() => {
    const container = scrollableContainerRef.current
    if (container?.current) {
      // Scroll custom scrollable container
      container.current.scrollTo(0, 0)
    } else {
      // Fall back to window scrolling
      window.scrollTo(0, 0)
    }
  }, [pathname])

  const contextValue: ScrollableContainerContextType = {
    registerScrollableContainer,
    getScrollableContainer,
  }

  return <ScrollableContainerContext.Provider value={contextValue}>{children}</ScrollableContainerContext.Provider>
}
